/**
 * Every topic type's default share form, keyed by type id.
 *
 * Each type owns its `<type>/default_share_form.yaml` and its `SharingSetup`
 * imports that file directly; this is the lookup for code that has a type id
 * rather than a component — the share wizard reading a form's `goal` prose, say.
 * Globbed rather than listed so a new type's form is picked up by existing here.
 * Deep-import this module (not the package barrel) — it is data only, and the
 * barrel pulls in the whole per-type component registry.
 */
const shareFormYamls = import.meta.glob<string>('./*/default_share_form.yaml', {
  query: '?raw',
  import: 'default',
  eager: true,
});

const byTopicTypeId: Record<string, string> = {};
for (const [path, yamlSource] of Object.entries(shareFormYamls)) {
  const match = path.match(/\.\/([a-z]+)\/default_share_form\.yaml$/);
  if (match) byTopicTypeId[match[1]] = yamlSource;
}

/**
 * Test escape hatch, mirroring `__TEST_TEMPLATES_YAML__` for the classic files:
 * E2E tests inject a stable share form via `window.__TEST_SHARE_FORM_YAML__`
 * (set in `Cypress.on('window:before:load')`) so wizard walk-throughs don't
 * break when a shipped `default_share_form.yaml` evolves. When set it stands in
 * for every type, which is what the classic override does too.
 *
 * Read per call rather than once at module load: unlike the classic files,
 * which are parsed at import time, this lookup runs on demand, so reading it
 * here keeps the two overrides interchangeable from a test's point of view.
 */
type TestWindow = Window & { __TEST_SHARE_FORM_YAML__?: string };

function testOverride(): string | undefined {
  return typeof window !== 'undefined'
    ? (window as TestWindow).__TEST_SHARE_FORM_YAML__ || undefined
    : undefined;
}

/** The type's default share form, or '' for a type that ships none. */
export function defaultShareFormYaml(topicTypeId: string | undefined): string {
  const override = testOverride();
  if (override !== undefined) return override;
  return (topicTypeId && byTopicTypeId[topicTypeId]) || '';
}
